/**
 * MyAttendanceTab Component
 * Employee's personal attendance view with check-in/out functionality
 */
import { Clock, MapPin, CheckCircle, XCircle, AlertTriangle, Loader2, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

export const MyAttendanceTab = ({ 
  officeInfo,
  myAttendanceStatus,
  myAttendanceHistory,
  locationError,
  checkingIn,
  checkingOut,
  onCheckIn,
  onCheckOut
}) => {
  const formatTime = (timeStr) => {
    if (!timeStr) return '-';
    return new Date(timeStr).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  return (
    <div className="space-y-6">
      {/* Office Hours Info */}
      <div className="bg-gradient-to-r from-teal-50 to-blue-50 dark:from-teal-900/20 dark:to-blue-900/20 border-2 border-teal-200 dark:border-teal-800 rounded-2xl p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-teal-600" />
              Office Hours
            </h3>
            <div className="mt-2 space-y-1 text-sm text-gray-600 dark:text-gray-400">
              <p><strong>Working Days:</strong> Monday to Saturday</p>
              <p><strong>Timing:</strong> 10:00 AM - 6:00 PM (PKT)</p>
              <p><strong>Current Time (PKT):</strong> {officeInfo?.current_status?.current_time_pkt || '-'}</p>
            </div>
          </div>
          <div className={`px-4 py-2 rounded-lg ${
            officeInfo?.current_status?.allowed 
              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' 
              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
          }`}>
            {officeInfo?.current_status?.allowed ? (
              <span className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Office Open
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <XCircle className="w-4 h-4" />
                Office Closed
              </span>
            )}
          </div>
        </div>
        
        {/* Office Locations */}
        <div className="mt-4 pt-4 border-t border-teal-200 dark:border-teal-700">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Office Locations:</p>
          <div className="grid md:grid-cols-2 gap-3">
            {officeInfo?.locations?.map((loc, idx) => (
              <div key={idx} className="bg-white dark:bg-slate-800 rounded-lg p-3 text-sm">
                <p className="font-medium text-gray-800 dark:text-white">{loc.name}</p>
                <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">{loc.address}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Check In/Out Section */}
      <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Today&apos;s Attendance</h3>
        
        {/* Today's Status */}
        <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-4 mb-4">
          {myAttendanceStatus?.checked_in ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">Status</span>
                <Badge className="bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                  Checked In
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">Check-in Time</span>
                <span className="font-medium text-gray-800 dark:text-white">
                  {myAttendanceStatus.check_in_time_pkt || formatTime(myAttendanceStatus.check_in_time)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">Location</span>
                <span className="font-medium text-gray-800 dark:text-white">
                  {myAttendanceStatus.office_name || (myAttendanceStatus.location_verified ? 'Office' : 'Remote')}
                </span>
              </div>
              {myAttendanceStatus.check_out_time && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Check-out Time</span>
                  <span className="font-medium text-gray-800 dark:text-white">
                    {myAttendanceStatus.check_out_time_pkt || formatTime(myAttendanceStatus.check_out_time)}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <Clock className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
              <p className="text-gray-500 dark:text-gray-400">You have not checked in today</p>
            </div>
          )}
        </div>

        {/* Error Message */}
        {locationError && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4">
            <p className="text-sm text-red-700 dark:text-red-300 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              {locationError}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          {!myAttendanceStatus?.checked_in ? (
            <Button 
              onClick={onCheckIn}
              disabled={checkingIn || !officeInfo?.current_status?.allowed}
              className="flex-1 bg-teal-600 hover:bg-teal-700 text-white font-semibold"
              data-testid="employee-check-in-btn"
            >
              {checkingIn ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <MapPin className="w-4 h-4 mr-2" />
              )}
              Check In
            </Button>
          ) : !myAttendanceStatus?.check_out_time ? (
            <Button 
              onClick={onCheckOut}
              disabled={checkingOut}
              className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-semibold"
              data-testid="employee-check-out-btn"
            >
              {checkingOut ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Clock className="w-4 h-4 mr-2" />
              )}
              Check Out
            </Button>
          ) : (
            <div className="flex-1 text-center py-3 bg-gray-100 dark:bg-slate-700 rounded-lg">
              <p className="text-gray-600 dark:text-gray-400">
                <CheckCircle className="w-4 h-4 inline mr-2 text-green-500" />
                Attendance completed for today
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Attendance History */}
      <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Attendance History</h3>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-slate-600">
                <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Date</th>
                <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Check In</th>
                <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Check Out</th>
                <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Hours</th>
                <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {(!Array.isArray(myAttendanceHistory) || myAttendanceHistory.length === 0) ? (
                <tr>
                  <td colSpan="5" className="py-8 text-center">
                    <Calendar className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                    <p className="text-gray-500 dark:text-gray-400">No attendance history yet</p>
                  </td>
                </tr>
              ) : (
                myAttendanceHistory.slice(0, 15).map((record) => (
                  <tr key={record.id} className="border-b border-gray-100 dark:border-slate-700">
                    <td className="py-3 px-4 font-medium text-gray-800 dark:text-white">
                      {record.check_in_date_pkt || new Date(record.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                      {record.check_in_time_pkt || formatTime(record.check_in_time)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                      {record.check_out_time_pkt || formatTime(record.check_out_time)}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                      {record.total_hours ? `${record.total_hours}h` : '-'}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1">
                        <Badge className={`text-xs ${
                          record.location_status === 'present' || record.check_in_location?.verified ? 'bg-green-100 text-green-600' : 'bg-orange-100 text-orange-600'
                        }`}>
                          {record.check_in_location?.office_name || (record.location_status === 'present' ? 'Office' : 'Remote')}
                        </Badge>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MyAttendanceTab;
